import { useEffect, useState } from "react";
import { Search, X, SlidersHorizontal } from "lucide-react";
import clsx from "clsx";
import { useSites, useEventTypes, type CaseFilters } from "../../lib/queries";
import { Button } from "../../components/ui";
import EntitySelect from "../../components/admin/EntitySelect";
import { SEVERITY_COLOR } from "../../lib/format";
import type { Severity } from "../../types/api";

const SEVERITIES = ["advisory", "low", "medium", "high", "critical"] as const;
const STATUSES = ["new", "acknowledged", "in_review", "classified", "actioned", "closed"] as const;
const MEDIA = [
  { v: "", label: "Any media" },
  { v: "video", label: "Video" },
  { v: "image", label: "Image" },
  { v: "telemetry", label: "Telemetry only" },
];

function toggle<T>(arr: T[] | undefined, v: T): T[] {
  const a = arr ?? [];
  return a.includes(v) ? a.filter((x) => x !== v) : [...a, v];
}

export default function CasesFilters({
  filters,
  onChange,
  onClear,
}: {
  filters: CaseFilters;
  onChange: (part: Partial<CaseFilters>) => void;
  onClear: () => void;
}) {
  const sites = useSites();
  const eventTypes = useEventTypes();

  // Debounce the free-text search so we don't refetch per keystroke.
  const [search, setSearch] = useState(filters.search ?? "");
  useEffect(() => setSearch(filters.search ?? ""), [filters.search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((search || undefined) !== (filters.search || undefined)) onChange({ search: search || undefined });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const inputCls =
    "rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-sm text-ink outline-none focus:border-aurora-cyan/50";
  const activeCount =
    (filters.severity?.length ?? 0) + (filters.status?.length ?? 0) + (filters.event_type?.length ?? 0) +
    (filters.site ? 1 : 0) + (filters.asset ? 1 : 0) + (filters.media_type ? 1 : 0) +
    (filters.has_media ? 1 : 0) + (filters.dead_man ? 1 : 0) + (filters.min_risk ? 1 : 0) +
    (filters.search ? 1 : 0);

  return (
    <div className="glass space-y-3 p-3">
      {/* Row 1: search + entity selects */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slatey" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search asset registration or site…"
            className="w-full rounded-lg border border-white/10 bg-white/5 py-1.5 pl-9 pr-3 text-sm outline-none focus:border-aurora-cyan/40"
          />
        </div>

        <select
          value={filters.site ?? ""}
          onChange={(e) => onChange({ site: e.target.value ? Number(e.target.value) : "" })}
          className={inputCls}
        >
          <option value="">All sites</option>
          {(sites.data ?? []).map((s) => (
            <option key={s.id} value={s.id} className="bg-ocean-800">{s.title}</option>
          ))}
        </select>

        <div className="min-w-[180px]">
          <EntitySelect
            reference={{ endpoint: "assets", optionLabel: "registration" }}
            value={(filters.asset as number) || null}
            onChange={(id) => onChange({ asset: id ?? "" })}
            placeholder="All assets"
          />
        </div>

        <select
          value={filters.media_type ?? ""}
          onChange={(e) => onChange({ media_type: e.target.value || undefined })}
          className={inputCls}
        >
          {MEDIA.map((m) => (
            <option key={m.v} value={m.v} className="bg-ocean-800">{m.label}</option>
          ))}
        </select>

        {activeCount > 0 && (
          <Button variant="ghost" onClick={onClear}>
            <span className="inline-flex items-center gap-1.5"><X size={13} /> Clear ({activeCount})</span>
          </Button>
        )}
      </div>

      {/* Row 2: severity + status chips */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="section-tag mr-1">SEVERITY</span>
          {SEVERITIES.map((s) => {
            const on = filters.severity?.includes(s);
            return (
              <button
                key={s}
                onClick={() => onChange({ severity: toggle(filters.severity, s) })}
                className={clsx("rounded-full px-2 py-0.5 text-[11px] uppercase tracking-wide transition", on ? "text-ink" : "text-slatey hover:text-ink")}
                style={on ? { background: `${SEVERITY_COLOR[s as Severity]}26`, border: `1px solid ${SEVERITY_COLOR[s as Severity]}66` } : { border: "1px solid rgba(255,255,255,0.1)" }}
              >
                {s}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="section-tag mr-1">STATUS</span>
          {STATUSES.map((s) => {
            const on = filters.status?.includes(s);
            return (
              <button
                key={s}
                onClick={() => onChange({ status: toggle(filters.status, s) })}
                className={clsx(
                  "rounded-full border px-2 py-0.5 text-[11px] transition",
                  on ? "border-aurora-cyan/50 bg-aurora-cyan/10 text-aurora-cyan" : "border-white/10 text-slatey hover:text-ink"
                )}
              >
                {s.replace("_", " ")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 3: event types + toggles + risk slider */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="section-tag mr-1">TYPE</span>
          {(eventTypes.data ?? []).map((t) => {
            const on = filters.event_type?.includes(t.id);
            return (
              <button
                key={t.id}
                onClick={() => onChange({ event_type: toggle(filters.event_type, t.id) })}
                className={clsx(
                  "rounded-full border px-2 py-0.5 text-[11px] transition",
                  on ? "border-pacific/50 bg-pacific/15 text-pacific" : "border-white/10 text-slatey hover:text-ink"
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <label className="flex items-center gap-1.5 text-xs text-slatey">
          <input type="checkbox" checked={!!filters.has_media} onChange={(e) => onChange({ has_media: e.target.checked || undefined })} />
          Has media
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slatey">
          <input type="checkbox" checked={!!filters.dead_man} onChange={(e) => onChange({ dead_man: e.target.checked || undefined })} />
          Dead-man
        </label>

        <div className="flex items-center gap-2 text-xs text-slatey">
          <SlidersHorizontal size={13} />
          <span>Min risk</span>
          <input
            type="range" min={0} max={100} step={5}
            value={filters.min_risk ?? 0}
            onChange={(e) => onChange({ min_risk: Number(e.target.value) || undefined })}
            className="w-28 accent-[#22D3EE]"
          />
          <span className="mono w-8 text-ink">{filters.min_risk ?? 0}</span>
        </div>
      </div>
    </div>
  );
}
